import { useState, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Package, X, RefreshCw, ChevronRight } from 'lucide-react'
import { getStoredTrackingTokens, clearStoredTrackingToken, clearOldTerminalTokens, removeStoredTrackingToken, StoredTrackingToken } from '@/utils/trackingStorage'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { AnimatePresence, m } from 'framer-motion'
import { cn } from '@/utils/helpers'

/**
 * Baner pokazywany, gdy w localStorage są tokeny śledzenia zamówienia.
 * Umożliwia szybki dostęp do statusu i odświeżanie danych.
 * Obsługuje wiele zamówień jednocześnie.
 */
export function TrackOrderBanner() {
  const { t } = useTranslation()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [storedTokens, setStoredTokens] = useState<StoredTrackingToken[]>([])
  const [isExpanded, setIsExpanded] = useState(false)

  const refreshTokens = () => {
    clearOldTerminalTokens()
    setStoredTokens(getStoredTrackingTokens())
  }

  useEffect(() => {
    refreshTokens()
  }, [location.pathname])

  // Znajdź token, który obecnie oglądamy (jeśli jesteśmy na stronie śledzenia)
  const currentTokenFromUrl = useMemo(() => {
    const match = location.pathname.match(/\/sledzenie\/([^/]+)/)
    return match ? match[1] : null
  }, [location.pathname])

  const showBanner = storedTokens.length > 0
  
  const handleDismissAll = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    clearStoredTrackingToken()
    setStoredTokens([])
  }

  const handleDismissOne = (e: React.MouseEvent, token: string) => {
    e.preventDefault()
    e.stopPropagation()
    removeStoredTrackingToken(token)
    setStoredTokens(prev => prev.filter(t => t.token !== token))
  }

  const handleRefresh = (token: string) => {
    queryClient.invalidateQueries({ queryKey: ['order-tracking', token] })
  }

  if (!showBanner) return null

  // Jeśli jest tylko jedno zamówienie, używamy uproszczonego widoku
  if (storedTokens.length === 1) {
    const token = storedTokens[0].token
    const isOnThisTrackingPage = currentTokenFromUrl === token

    return (
      <div className="fixed bottom-0 left-0 right-0 z-[1000] px-4 pb-4 md:pb-6 pointer-events-none">
        <m.div 
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="pointer-events-auto max-w-2xl mx-auto bg-primary text-white rounded-2xl shadow-lg shadow-primary/30 p-4 md:p-5 flex items-center gap-4"
        >
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
            <Package className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-base md:text-lg">
              {isOnThisTrackingPage ? t('statusAktualny', 'Aktualny status') : t('sledzZamowienie', 'Śledź zamówienie')}
            </p>
            <p className="text-white/90 text-sm mt-0.5 truncate">
              {isOnThisTrackingPage 
                ? t('kliknijAbyOdswiezyc', 'Kliknij przycisk, aby odświeżyć status.')
                : t('maszAktywneZamowienie', 'Masz aktywne zamówienie – sprawdź status.')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to={`/sledzenie/${token}`}
              onClick={() => handleRefresh(token)}
              className="px-4 py-2.5 bg-white text-primary font-bold rounded-xl hover:bg-white/90 transition-colors text-sm whitespace-nowrap flex items-center gap-2"
            >
              {isOnThisTrackingPage ? (
                <>
                  <RefreshCw className="w-4 h-4" />
                  {t('odswiez', 'Odśwież')}
                </>
              ) : (
                t('otworzSledzenie', 'Otwórz śledzenie')
              )}
            </Link>
            <button
              type="button"
              onClick={handleDismissAll}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
              aria-label={t('zamknij', 'Zamknij')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </m.div>
      </div>
    )
  }

  // Widok dla wielu zamówień
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[1000] px-4 pb-4 md:pb-6 pointer-events-none">
      <div className="max-w-2xl mx-auto pointer-events-auto">
        <AnimatePresence>
          {isExpanded && (
            <m.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-white rounded-t-2xl shadow-xl overflow-hidden mb-[-1px] border border-gray-100"
            >
              <div className="p-4 border-b border-gray-50 flex justify-between items-center">
                <span className="font-black text-gray-900 uppercase text-xs tracking-widest">{t('twojeZamowienia', 'Twoje zamówienia')}</span>
                <button onClick={() => setIsExpanded(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {storedTokens.map((st) => (
                  <div key={st.token} className="border-b border-gray-50 last:border-0">
                    <Link
                      to={`/sledzenie/${st.token}`}
                      onClick={() => {
                        handleRefresh(st.token)
                        setIsExpanded(false)
                      }}
                      className={cn(
                        "flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors",
                        currentTokenFromUrl === st.token && "bg-primary/5"
                      )}
                    >
                      <div className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center",
                        currentTokenFromUrl === st.token ? "bg-primary text-white" : "bg-gray-100 text-gray-400"
                      )}>
                        <Package className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 text-sm truncate">
                          Zamówienie {st.token.slice(0, 8)}...
                        </p>
                        <p className="text-gray-500 text-xs mt-0.5">
                          {new Date(st.addedAt).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                         <ChevronRight className="w-4 h-4 text-gray-300" />
                         <button 
                           onClick={(e) => handleDismissOne(e, st.token)}
                           className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 transition-colors"
                         >
                           <X className="w-4 h-4" />
                         </button>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </m.div>
          )}
        </AnimatePresence>

        <m.div 
          layout
          className={cn(
            "bg-primary text-white shadow-lg shadow-primary/30 p-4 md:p-5 flex items-center gap-4 transition-all duration-300",
            isExpanded ? "rounded-b-2xl" : "rounded-2xl"
          )}
        >
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
            <Package className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
            <p className="font-bold text-base md:text-lg">
              {t('aktywneZamowienia', 'Aktywne zamówienia')} ({storedTokens.length})
            </p>
            <p className="text-white/90 text-sm mt-0.5 truncate">
              {isExpanded ? t('wybierzZamowienie', 'Wybierz zamówienie z listy') : t('kliknijAbyZobaczycWszystkie', 'Kliknij, aby zobaczyć wszystkie.')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="px-4 py-2.5 bg-white text-primary font-bold rounded-xl hover:bg-white/90 transition-colors text-sm whitespace-nowrap"
            >
              {isExpanded ? t('zamknijListę', 'Zamknij listę') : t('pokazWszystkie', 'Pokaż wszystkie')}
            </button>
            <button
              type="button"
              onClick={handleDismissAll}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
              aria-label={t('zamknij', 'Zamknij')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </m.div>
      </div>
    </div>
  )
}


